<template>
  <div v-if="hasPayouts" class="flex flex-col"> 
    <div class="fixed-section sticky top-[118px] w-full bg-gray-50 z-20">
      <!-- earning-history 區塊 -->
      <div class="earning-history mt-4 grid grid-cols-6 gap-4 mt-4 ">
        <!-- 上次撥款 -->
        <div class="text-14px px-4 py-2 rounded-8px bg-gray-600 col-span-2">
          <div class='title flex justify-between items-center'>
            <span class='text-14px'>{{ $t('profiles.payout_lists.previous_payout') }}</span>
            <span class='text-12px'>
              {{ latestPaidPayout ? formatMonth(latestPaidPayout.paid_at) : '-' }}
            </span>
          </div>
          <div class='content flex justify-between items-center mt-4'>
            <span class='text-20px lg:text-32px'>
              {{ latestPaidPayout ? currency(latestPaidPayout.total_price) : '-' }}
            </span>
            <span
              :style="getPaymentStateStyle(latestPaidPayout ? latestPaidPayout.payment_state : 'Unpaid')"
              class="rounded-3xl px-2 py-0.5 text-12px border"
            >
              {{ latestPaidPayout ? latestPaidPayout.payment_state : 'Unpaid' }}
            </span>
          </div>
        </div>
        <!-- 下次撥款 -->
        <div class="text-14px px-4 py-2 rounded-8px bg-gray-600 col-span-2">
          <div class='title flex justify-between items-center'>
            <span class='text-14px'>{{ $t('profiles.payout_lists.next_payout') }}</span>
            <span class='text-12px'>
              <template v-if="nextPaidPayout">
                <template v-if="nextPaidPayout.payment_state === 'Unpaid'">-</template>
                <template v-else>{{ formatMonth(nextPaidPayout.updated_at) }}</template>
              </template>
              <template v-else>-</template>
            </span>
          </div>
          <div class='content flex justify-between items-center mt-4'>
            <span class='text-20px lg:text-32px'>
              {{ nextPaidPayout ? currency(nextPaidPayout.total_price) : '-' }}
            </span>
            <span
              :style="getPaymentStateStyle(nextPaidPayout ? nextPaidPayout.payment_state : 'Unpaid')"
              class="rounded-3xl px-2 py-0.5 text-12px border"
            >
              {{ nextPaidPayout ? nextPaidPayout.payment_state : 'Unpaid' }}
            </span>
          </div>
        </div>
        <!-- 餘額 -->
        <div class="text-14px px-4 py-2 rounded-8px bg-gray-600 col-span-2">
          <div class='title flex justify-between items-center'>
            <span class='text-14px'>{{ $t('profiles.payout_items.balance') }}</span>
          </div>
          <div class='content flex justify-between items-center mt-4'>
            <span class='text-20px lg:text-32px'>{{ currency(payoutBalance) }}</span>
          </div>
        </div>
      </div>
      <!-- 標題 -->
      <div class="text-12px font-[600] text-grey-3 mt-4 grid gap-x-2 lg:text-center xl:text-left
                    grid-cols-[30fr_24fr_24fr_30fr_20fr_16fr] 
                    lg:grid-cols-[175px_24fr_24fr_30fr_24fr_26fr]
                    xl:grid-cols-[49.5fr_30fr_24fr_30fr_24fr_26fr]">
        <div class='h-5 text-left'>{{ $t('profiles.payout_lists.period') }}</div>
        <div class='h-5'>{{ $t('profiles.payout_lists.total_revenues') }}</div>
        <div class='h-5'>{{ $t('profiles.payout_lists.platform_fee') }}</div>
        <div class='h-5'>{{ $t('profiles.payout_lists.pv_commission') }}</div>
        <div class='h-5'>{{ $t('profiles.payout_lists.profit') }}</div>
        <div class='h-5'>{{ $t('profiles.payout_lists.payment_status') }}</div>
        <hr class='col-span-6 border-gray-500 my-2'>
      </div>
      </div>
    <div class="payouts-list">
      <div class="flex-1 pr-1 -mr-3">
        <payout-item
          v-for="(payout, index) in stateData.items"
          :key="payout.id"
          :payout="payout"
          :is-last="index === stateData.items.length - 1"
        />
        <p v-if="state.loading"
           class="text-16px flex justify-center items-center h-12">
          <img src="../../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
        </p>
      </div>
    </div>
  </div>
  <div v-else class="flex flex-col flex-1 items-center justify-center mt-[15%] lg:mt-[20%] 2xl:mt-[15%]">
    <img src="../../images/ClipboardText.svg" class="block w-12 h12 mb-4" />
    <p class="text-16px font-medium text-center">
      {{ $t('profiles.payout_lists.no_payout_history') }}
    </p>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, onUnmounted } from 'vue';
import { debounce } from 'lodash';

export default defineComponent({ 
  setup(props) {
    const stateData = reactive({
      items: [],
      totalCount: 0
    });

    const latestPaidPayout = ref(null);
    const nextPaidPayout = ref(null);
    const payoutBalance = ref(0);

    const state = reactive({
      loading: false,
      initialLoading: true,
      itemsCount: 10,
    });

    const pageInfo = reactive({
      page: 1,
      hasMore: true,
      totalPages: 1
    });

    const hasPayouts = ref(false);
  
    const scrollListener = ref(null);

    // 輔助方法
    const currency = (val) => {
      if (val == null) return '-';
      return new Intl.NumberFormat('zh-TW', { style: 'currency', currency: 'TWD' }).format(val);
    };
    const formatMonth = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    };
    const getPaymentStateStyle = (state) => {
      return {
        color: `var(--color-payout-state-${state.toLowerCase()})`,
        background: `color-mix(in srgb, var(--color-payout-state-${state.toLowerCase()}), transparent 80%)`,
        borderColor: `var(--color-payout-state-${state.toLowerCase()})`
      };
    };

    const checkAndLoadMore = (page, perPage) => {
      if (!pageInfo.hasMore || state.loading) return;
      
      // 檢查頁面內容是否足夠產生滾動條
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      
      // 如果頁面高度不足以產生滾動條，繼續加載更多數據
      if (documentHeight <= windowHeight) {
        fetchPayouts(pageInfo.page, perPage);
      }
    };

    const fetchPayouts = async (page, perPage) => {
      if (!pageInfo.hasMore || state.loading) return;

      state.loading = true;
      const url = `/api/v1/payouts`;
      const params = new URLSearchParams({
        page: page,
        per_page: perPage,
      });

      try {
        const response = await fetch(`${url}?${params}`);
        if (response.ok) {
          const data = await response.json();

          // earning-history summary
          latestPaidPayout.value = data.latest_paid_payout;
          nextPaidPayout.value = data.next_paid_payout;
          payoutBalance.value = data.payout_balance;
          
          // 列表
          stateData.totalCount = data.pagination.total_count;

          const newItems = data.data;
          newItems.forEach(item => {
            if (!stateData.items.some(existingItem => existingItem.id === item.id)) {
              stateData.items.push(item);
            }
          });

          // 更新分頁信息
          pageInfo.totalPages = data.pagination.total_pages;
          pageInfo.hasMore = pageInfo.page < data.pagination.total_pages;
          pageInfo.page++;

          hasPayouts.value = stateData.items.length > 0;

          // 如果獲取的項目少於每頁數量，且還有更多數據，則繼續獲取
          if (newItems.length < perPage && pageInfo.hasMore) {
            fetchPayouts(pageInfo.page, perPage);
          } else {
            // 延遲檢查頁面高度，確保DOM已更新
            setTimeout(() => {
              checkAndLoadMore(page, perPage);
            }, 100);
          }
        }
      } catch (error) {
        console.error('Error fetching payouts:', error);
        pageInfo.hasMore = false;
      } finally {
        state.loading = false;
        state.initialLoading = false;
      }
    }; 

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    const addScrollListener = () => {
      removeScrollListener();
      scrollListener.value = handleScroll;
      window.addEventListener('scroll', scrollListener.value, { passive: true });
    };

    const removeScrollListener = () => {
      if (scrollListener.value) {
        window.removeEventListener('scroll', scrollListener.value);
        scrollListener.value = null;
      }
    };

    const handleScroll = debounce(() => {
      if (!pageInfo.hasMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrolledPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      
      if (scrolledPercentage > 30 && !state.loading) {
        fetchPayouts(pageInfo.page, state.itemsCount);
      }
    }, 100);
    
    onMounted(async () => {
      await fetchPayouts(1, state.itemsCount);
      addScrollListener();
    });
    
    onUnmounted(() => {
      removeScrollListener();
    });
    
    return {
      stateData,
      state,
      hasPayouts,
      latestPaidPayout,
      nextPaidPayout,
      payoutBalance,
      currency,
      formatMonth,
      getPaymentStateStyle,
    };
  }
});
</script>